import { useEffect, useRef } from "react";
import { useTheme } from "@/components/ui/theme-provider";
import { useUserPreferences } from "@/hooks/useUserPreferences";

/**
 * Bridges the DB-backed user_preferences theme into the canonical ThemeProvider.
 * Mounted once near the root, inside the ThemeProvider subtree.
 *
 * - On initial preferences load (post sign-in), if the saved DB theme differs
 *   from the current provider theme, hydrate the provider with the DB value.
 * - Runs only once per session to avoid fighting the user's manual toggle.
 */
export const ThemePreferenceSync = () => {
  const { theme, setTheme } = useTheme();
  const { preferences, isLoading } = useUserPreferences();
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (isLoading || hydratedRef.current) return;
    const dbTheme = preferences.theme;
    if (!dbTheme) return;
    if (dbTheme !== theme) {
      setTheme(dbTheme);
    }
    hydratedRef.current = true;
  }, [isLoading, preferences.theme, theme, setTheme]);

  return null;
};

export default ThemePreferenceSync;
